import React from 'react';
import { motion } from 'framer-motion';
import { GiftIcon } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
type Bonus = {
  id: string;
  title: string;
  value: string;
  image: string;
  description: string;
  payoff: string;
};
const bonuses: Bonus[] = [{
  id: '01',
  title: '100 Ready-to-Teach Ballet Combinations',
  value: '12',
  image: 'https://cdn.magicpatterns.com/uploads/nWa9yXs2ufNVed6mdQiKbZ/seguridad_e_higiene_(11).png',
  description: 'A bank of complete barre, center, turns, jumps, across-the-floor, musicality, arm coordination, and strength combinations—with musical counts, teacher cues, corrections, and variations.',
  payoff: 'No preparation needed. Open, choose, and teach.'
}, {
  id: '02',
  title: 'Ballet Corrections and Teacher Cueing Library',
  value: '12',
  image: 'https://cdn.magicpatterns.com/uploads/vTd94paAYK1jNY7G7WXLYx/seguridad_e_higiene_(9).png',
  description: 'Professional, ready-to-say cues and practical corrections for posture, alignment, feet, balance, barre, center, jumps, turns, musicality, and expression.',
  payoff: 'The words are already there. You just say them.'
}, {
  id: '03',
  title: '50 Creative Pre-Ballet Activities',
  value: '10',
  image: 'https://cdn.magicpatterns.com/uploads/3VioUmhGjK5Wez9bjk8qQr/seguridad_e_higiene_(10).png',
  description: 'Playful, developmental movement, musicality, coordination, and imagination activities for ages 3–6, each with objective, duration, steps, music suggestions, and adaptations.',
  payoff: 'Have the right activity ready before the class begins.'
}, {
  id: '04',
  title: 'Ballet Recital and Rehearsal Planner',
  value: '12',
  image: 'https://cdn.magicpatterns.com/uploads/amjAABewyyx5DxpP5gs13C/seguridad_e_higiene_(8).png',
  description: 'Planning, scheduling, and organization tools for recitals and studio performances: calendars, rehearsal schedules, costume and music lists, stage checklists, and family communication.',
  payoff: 'Open, organize, perform—without the last-minute panic.'
}, {
  id: '05',
  title: 'Ballet Progress Tracking and Assessment System',
  value: '12',
  image: 'https://cdn.magicpatterns.com/uploads/dZQWEf2GfLftLVYSFg4ZGY/seguridad_e_higiene_(7).png',
  description: 'Level-based assessments, skill rubrics, progress sheets, parent reports, checklists, correction criteria, and improvement plans for every dancer.',
  payoff: 'The system tracks the progress. You focus on teaching.'
}];
export function BonusSection() {
  return <section className="border-y border-ballet-line bg-ballet-blush/45 px-4 py-20 sm:py-28" data-id="element-17" data-mp="176it5haquj01">
      <div className="mx-auto max-w-6xl" data-id="element-18" data-mp="12d7tq189h4i5">
        <div className="text-center" data-id="element-19" data-mp="eoogr8e9o3jq">
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-ballet-rose" data-id="element-20" data-mp="enlr13sok75e">
            Included at no additional cost
          </p>
          <h2 className="mt-3 font-display text-5xl font-semibold text-ballet-ink" data-id="element-21" data-mp="jej83dxu3vh">
            The 5 Strategic Bonuses
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-ballet-muted" data-id="element-22" data-mp="5b69sdx3g9lm">
            Every bonus is included to make your teaching week lighter, clearer,
            and more beautifully organized.
          </p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-23" data-mp="3rnl9qyqjn7dk">
          {bonuses.map((bonus, index) => <motion.article key={bonus.id} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.1
        }} transition={{
          delay: index * 0.05
        }} className={`overflow-hidden rounded-2xl border border-ballet-line bg-white shadow-card ${index === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`} data-id="element-24" data-mp="o8z10iycuv36" data-mpi={index}>
              <div className="relative flex h-56 items-end justify-center overflow-hidden bg-ballet-canvas p-5" data-id="element-25" data-mp="3ahgb1sktxmou">
                <img src={bonus.image} alt={`${bonus.title} bonus guide`} className="h-full max-w-full object-contain" data-id="element-26" data-mp="35i3o77tykfm9" />
                <span className="absolute left-4 top-4 rounded-full border border-ballet-gold/60 bg-white px-3 py-1 text-xs font-bold text-ballet-rose" data-id="element-27" data-mp="21mrf4v7bv3k">
                  BONUS {bonus.id}
                </span>
              </div>
              <div className="p-6" data-id="element-28" data-mp="3o81t5f2glslo">
                <div className="mb-3 flex items-center gap-2 text-ballet-gold" data-id="element-29" data-mp="3o70aaof7c1te">
                  <GiftIcon size={18} data-id="element-30" data-mp="3j8yss7xfcak1" />
                  <span className="text-xs font-bold uppercase tracking-[0.12em]" data-id="element-31" data-mp="3jc4i9o9ytega">
                    Included bonus
                  </span>
                </div>
                <h3 className="font-display text-3xl font-semibold leading-tight text-ballet-ink" data-id="element-32" data-mp="27t1q7gwnxs4c">
                  {bonus.title}
                </h3>
                <p className="mt-3 inline-flex rounded-full bg-ballet-goldSoft px-3 py-1 text-xs font-semibold text-ballet-ink" data-id="element-33" data-mp="sbdryy8w6hbv">
                  Real value:{' '}
                  <span className="ml-1 line-through" data-id="element-34" data-mp="x8nf278t6gwj">${bonus.value}</span>
                  <span className="ml-2 text-ballet-rose" data-id="element-35" data-mp="1228e82m51gxb">→ FREE</span>
                </p>
                <p className="mt-4 text-sm leading-6 text-ballet-muted" data-id="element-36" data-mp="16zru16yhi1rd">
                  {bonus.description}
                </p>
                <p className="mt-5 text-sm font-semibold italic text-ballet-rose" data-id="element-37" data-mp="1b47zx883l96l">
                  {bonus.payoff}
                </p>
              </div>
            </motion.article>)}
        </div>
        <div className="mx-auto mt-10 max-w-xl rounded-2xl border border-ballet-gold/60 bg-white p-7 text-center shadow-card" data-id="element-38" data-mp="1kxykw350n1hj">
          <p className="text-sm text-ballet-muted" data-id="element-39" data-mp="3mr5xv5x5p709">
            Total value of the 5 bonuses
          </p>
          <p className="mt-2 text-2xl text-ballet-muted line-through" data-id="element-40" data-mp="3eqgid9ns93ap">$58.00</p>
          <p className="mt-5 text-sm font-semibold text-ballet-ink" data-id="element-41" data-mp="3apeh6nab2h63">
            Your price today
          </p>
          <p className="mt-1 font-display text-5xl font-semibold text-ballet-rose" data-id="element-42" data-mp="3opp6hlvyvnp5">
            FREE
          </p>
          <p className="mt-2 text-sm text-ballet-muted" data-id="element-43" data-mp="3jptaa8tih0k5">
            Only included in this offer.
          </p>
        </div>
        <CountdownTimer data-id="element-44" data-mp="1iwxd5h73ya5" />
      </div>
    </section>;
}